import {
  emitTrackAcFilterEvent,
  emitTrackBathroomFilterEvent,
  emitTrackBedroomFilterEvent,
  emitTrackDishwasherFilterEvent,
  emitTrackDoormanFilterEvent,
  emitTrackFurnishedEventEvent,
  emitTrackGatedFilterEvent,
  emitTrackGymFilterEvent,
  emitTrackHeatingFilterEvent,
  emitTrackHighPriceFilterEvent,
  emitTrackLaundryFilterEvent,
  emitTrackLowPriceFilterEvent,
  emitTrackParkingFilterEvent,
  emitTrackPetsFilterEvent,
  emitTrackPoolFilterEvent,
} from '@/src/analytics/hotpads/events';
import type { AmenityType, FilterState } from '@/src/lib/filters';

export type FilterTrackingContext = {
  triggerLocation: 'hp_homepage' | 'post_contact_popup' | 'search_results';
  triggerObject: 'filter_panel' | 'hp_home_page_component|search' | 'info_cards' | 'post_contact_popup|alerts_modal';
};

export const HOMEPAGE_FILTER_TRACKING: FilterTrackingContext = {
  triggerLocation: 'hp_homepage',
  triggerObject: 'hp_home_page_component|search',
};

export const SRP_FILTER_TRACKING: FilterTrackingContext = {
  triggerLocation: 'search_results',
  triggerObject: 'filter_panel',
};

const amenityEventByType: Partial<Record<AmenityType, (context: FilterTrackingContext) => void>> = {
  cooling: emitTrackAcFilterEvent,
  dishwasher: emitTrackDishwasherFilterEvent,
  doorman: emitTrackDoormanFilterEvent,
  fitnessCenter: emitTrackGymFilterEvent,
  gatedEntry: emitTrackGatedFilterEvent,
  heating: emitTrackHeatingFilterEvent,
  parking: emitTrackParkingFilterEvent,
  swimmingPool: emitTrackPoolFilterEvent,
};

function arraysDiffer<T>(left: Array<T>, right: Array<T>) {
  if (left.length !== right.length) return true;
  return left.some((value) => !right.includes(value));
}

function valuesDiffer<T>(left: T, right: T) {
  return JSON.stringify(left) !== JSON.stringify(right);
}

export function emitSearchFilterChangeEvents(
  previousFilters: FilterState,
  nextFilters: FilterState,
  { triggerLocation, triggerObject }: FilterTrackingContext,
) {
  if (valuesDiffer(previousFilters.bedrooms, nextFilters.bedrooms)) {
    emitTrackBedroomFilterEvent({ triggerLocation, triggerObject });
  }

  if (valuesDiffer(previousFilters.bathrooms, nextFilters.bathrooms)) {
    emitTrackBathroomFilterEvent({ triggerLocation });
  }

  if (previousFilters.price.min !== nextFilters.price.min) {
    emitTrackLowPriceFilterEvent({ triggerLocation, triggerObject });
  }

  if (previousFilters.price.max !== nextFilters.price.max) {
    emitTrackHighPriceFilterEvent({ triggerLocation, triggerObject });
  }

  if (arraysDiffer(previousFilters.pets, nextFilters.pets)) {
    emitTrackPetsFilterEvent({ triggerLocation });
  }

  if (arraysDiffer(previousFilters.laundry, nextFilters.laundry)) {
    emitTrackLaundryFilterEvent({ triggerLocation });
  }

  if (previousFilters.furnished !== nextFilters.furnished) {
    emitTrackFurnishedEventEvent({ triggerLocation });
  }

  (Object.keys(amenityEventByType) as Array<AmenityType>).forEach((amenity) => {
    if (previousFilters.amenities.includes(amenity) !== nextFilters.amenities.includes(amenity)) {
      amenityEventByType[amenity]?.({ triggerLocation, triggerObject });
    }
  });
}
